import axios from 'axios';
import socketService from './socketService';

// Session manager to store session_id for API calls (deprecated - keeping for compatibility)
const sessionManager = {
  _sessionId: null,
  setSessionId(sessionId) {
    this._sessionId = sessionId;
  },
  getSessionId() {
    return this._sessionId;
  }
};

// Export session manager for use by components
export { sessionManager };

// Create axios instance
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://127.0.0.1:8000/api',
  timeout: 300000, // 5 minutes for long-running operations
  // Note: Removed 'Connection' header as it's forbidden by browsers
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    
    // Add Cypress test headers if running in Cypress
    if (window.Cypress) {
      config.headers['X-Test-Mode'] = 'true';
      config.headers['X-Test-DB-Name'] = 'ocr_matching_test';
      // Allow tests to override user via Cypress env
      const testUser = window.Cypress.env('TEST_USER') || 'gary';
      config.headers['X-Test-User'] = testUser;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle 401 Unauthorized - redirect to login (but not if already on login page)
    if (error.response?.status === 401 && !window.location.pathname.includes('/login')) {
      console.warn('Unauthorized access - redirecting to login');
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      // Use replace to avoid back button issues
      window.location.replace('/login');
      return Promise.reject(new Error('Unauthorized'));
    }
    
    // Handle 413 Content Too Large
    if (error.response?.status === 413) {
      console.error('File(s) too large:', error.response.data);
      return Promise.reject(new Error('Your files are too large to process. Please try to limit scans to 10MB or less.'));
    }
    
    // Handle common errors
    if (error.response?.status === 500) {
      console.error('Server error:', error.response.data);
    }
    return Promise.reject(error);
  }
);

// Individual Service APIs
export const extractionAPI = {
  // File upload extraction endpoint
  extractUpload: async (files, loanId = null, preview = true, selectedDocuments = null) => {
    const formData = new FormData();
    
    // Add all proposal files
    if (files.proposalFiles && Array.isArray(files.proposalFiles)) {
      for (const file of files.proposalFiles) {
        if (file instanceof File) {
          formData.append('proposal_files', file);
        } else if (file.fileKey && file.isUploadedFile) {
          // Handle Redux state objects by retrieving the actual File object
          const { getFile } = await import('../utils/fileStorage');
          const actualFile = getFile(file.fileKey);
          if (actualFile) {
            formData.append('proposal_files', actualFile);
          }
        }
      }
    } else if (files.proposalFile && files.proposalFile instanceof File) {
      // Fallback to singular file
      formData.append('proposal_files', files.proposalFile);
    } else if (files.proposalFile && files.proposalFile.fileKey && files.proposalFile.isUploadedFile) {
      // Handle Redux state object for singular file
      const { getFile } = await import('../utils/fileStorage');
      const actualFile = getFile(files.proposalFile.fileKey);
      if (actualFile) {
        formData.append('proposal_files', actualFile);
      }
    }
    
    // Add all note files
    if (files.noteFiles && Array.isArray(files.noteFiles)) {
      for (const file of files.noteFiles) {
        if (file instanceof File) {
          formData.append('note_files', file);
        } else if (file.fileKey && file.isUploadedFile) {
          // Handle Redux state objects by retrieving the actual File object
          const { getFile } = await import('../utils/fileStorage');
          const actualFile = getFile(file.fileKey);
          if (actualFile) {
            formData.append('note_files', actualFile);
          }
        }
      }
    } else if (files.noteFile && files.noteFile instanceof File) {
      // Fallback to singular file
      formData.append('note_files', files.noteFile);
    } else if (files.noteFile && files.noteFile.fileKey && files.noteFile.isUploadedFile) {
      // Handle Redux state object for singular file
      const { getFile } = await import('../utils/fileStorage');
      const actualFile = getFile(files.noteFile.fileKey);
      if (actualFile) {
        formData.append('note_files', actualFile);
      }
    }
    
    // Add all security files
    if (files.securityFiles && Array.isArray(files.securityFiles)) {
      for (const file of files.securityFiles) {
        if (file instanceof File) {
          formData.append('security_files', file);
        } else if (file.fileKey && file.isUploadedFile) {
          // Handle Redux state objects by retrieving the actual File object
          const { getFile } = await import('../utils/fileStorage');
          const actualFile = getFile(file.fileKey);
          if (actualFile) {
            formData.append('security_files', actualFile);
          }
        }
      }
    } else if (files.securityFile && files.securityFile instanceof File) {
      // Fallback to singular file
      formData.append('security_files', files.securityFile);
    } else if (files.securityFile && files.securityFile.fileKey && files.securityFile.isUploadedFile) {
      // Handle Redux state object for singular file
      const { getFile } = await import('../utils/fileStorage');
      const actualFile = getFile(files.securityFile.fileKey);
      if (actualFile) {
        formData.append('security_files', actualFile);
      }
    }
    
    // Add all additional files (unmatched files)
    if (files.additionalFiles && Array.isArray(files.additionalFiles)) {
      for (const file of files.additionalFiles) {
        if (file instanceof File) {
          formData.append('additional_files', file);
        } else if (file.fileKey && file.isUploadedFile) {
          // Handle Redux state objects by retrieving the actual File object
          const { getFile } = await import('../utils/fileStorage');
          const actualFile = getFile(file.fileKey);
          if (actualFile) {
            formData.append('additional_files', actualFile);
          }
        }
      }
    }
    
    // Add all archive files
    if (files.archiveFiles && Array.isArray(files.archiveFiles)) {
      for (const file of files.archiveFiles) {
        if (file instanceof File) {
          formData.append('archive_files', file);
        } else if (file.fileKey && file.isUploadedFile) {
          // Handle Redux state objects by retrieving the actual File object
          const { getFile } = await import('../utils/fileStorage');
          const actualFile = getFile(file.fileKey);
          if (actualFile) {
            formData.append('archive_files', actualFile);
          }
        }
      }
    }
    
    // Add all checklist files
    if (files.checklistFiles && Array.isArray(files.checklistFiles)) {
      for (const file of files.checklistFiles) {
        if (file instanceof File) {
          formData.append('checklist_files', file);
        } else if (file.fileKey && file.isUploadedFile) {
          // Handle Redux state objects by retrieving the actual File object
          const { getFile } = await import('../utils/fileStorage');
          const actualFile = getFile(file.fileKey);
          if (actualFile) {
            formData.append('checklist_files', actualFile);
          }
        }
      }
    }
    
    if (loanId) formData.append('loan_id', loanId);
    formData.append('preview', preview);
    formData.append('socket_id', socketService.socket.id);
    
    // Add selected documents if provided (for filtering which existing files to load)
    if (selectedDocuments && selectedDocuments.length > 0) {
      formData.append('selected_documents', JSON.stringify(selectedDocuments));
      console.log('📋 [API] Sending selected_documents:', selectedDocuments.length);
    }
    
    // Socket.IO handles connection management automatically
    
    return api.post('/extraction/extract-upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600000 // 10 minutes for extraction operations
    });
  },
  
  savePreview: (matchedResults, loanId) => {
    const formData = new FormData();
    formData.append('matched_results', JSON.stringify(matchedResults));
    formData.append('loan_id', loanId);
    formData.append('socket_id', socketService.socket.id);
    
    return api.post('/extraction/save-preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

export const matchingAPI = {
  match: (extractedFields, loanId = null) => {
    const formData = new FormData();
    formData.append('extracted_fields', JSON.stringify(extractedFields));
    if (loanId) formData.append('loan_id', loanId);
    
    return api.post('/matching/match', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

export const ocrResultsAPI = {
  save: (matchedResults, loanId) => {
    const formData = new FormData();
    formData.append('matched_results', JSON.stringify(matchedResults));
    formData.append('loan_id', loanId);
    
    return api.post('/ocr-results/save', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

// Documents API
export const documentsAPI = {
  extractValues: (files, loanId = null) => {
    const formData = new FormData();
    
    // Check if at least one file is provided
    const hasFiles = files.proposalFile || files.noteFile || files.securityFile || 
                     files.proposalFiles?.length > 0 || files.noteFiles?.length > 0 || 
                     files.securityFiles?.length > 0 || files.archiveFiles?.length > 0 || 
                     files.checklistFiles?.length > 0 || files.additionalFiles?.length > 0;
    if (!hasFiles) {
      return Promise.reject(new Error('At least one file must be provided'));
    }
    
    // Send primary files (for backward compatibility)
    if (files.proposalFile) formData.append('proposal_file_path', files.proposalFile);
    if (files.noteFile) formData.append('note_file_path', files.noteFile);
    if (files.securityFile) formData.append('security_file_path', files.securityFile);
    
    // Send all file arrays for multiple file support
    if (files.proposalFiles) {
      files.proposalFiles.forEach((file, index) => {
        formData.append(`proposal_files[${index}]`, file);
      });
    }
    if (files.noteFiles) {
      files.noteFiles.forEach((file, index) => {
        formData.append(`note_files[${index}]`, file);
      });
    }
    if (files.securityFiles) {
      files.securityFiles.forEach((file, index) => {
        formData.append(`security_files[${index}]`, file);
      });
    }
    if (files.archiveFiles) {
      files.archiveFiles.forEach((file, index) => {
        formData.append(`archive_files[${index}]`, file);
      });
    }
    if (files.checklistFiles) {
      files.checklistFiles.forEach((file, index) => {
        formData.append(`checklist_files[${index}]`, file);
      });
    }
    if (files.additionalFiles) {
      files.additionalFiles.forEach((file, index) => {
        formData.append(`additional_files[${index}]`, file);
      });
    }
    
    // Pass the loan ID if available
    if (loanId) {
      formData.append('loan_id', loanId);
    }
    
    return api.post('/documents/extract-from-paths', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  analyzeDocuments: (files) => {
    const formData = new FormData();
    
    if (files.proposalFile) formData.append('proposal_file', files.proposalFile);
    if (files.noteFile) formData.append('note_file', files.noteFile);
    if (files.securityFile) formData.append('security_file', files.securityFile);
    
    return api.post('/documents/analyze', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  processPDF: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    return api.post('/documents/process-pdf', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  autoPopulateFiles: (directoryPath) => {
    return api.post('/analysis/auto-populate', null, {
      params: { directory_path: directoryPath },
    });
  },
};

// Database API
export const databaseAPI = {
  initializeDatabase: () => {
    return api.post('/database/initialize');
  },
  
  getColumns: () => {
    return api.get('/database/columns');
  },
  
  getRecords: (loanId, column) => {
    return api.get('/database/records', {
      params: {
        loan_id: loanId,
        column: column,
      },
    });
  },

  getLoanDetails: (loanId, source = 'unknown') => {
    return api.get(`/database/loan/${loanId}?from=${source}`);
  },

  getLoanDocuments: (loanId, source = 'unknown') => {
    return api.get(`/database/loan/${loanId}/documents?from=${source}`);
  },

  getDocumentContent: (s3Key) => {
    return api.get(`/database/document/${s3Key}`);
  },

  clearLoanResults: (loanId) => {
    return api.delete(`/database/loan/${loanId}/results`);
  },

  getAllLoans: () => {
    return api.get('/database/loans');
  },

  getLoansForTypeahead: () => {
    return api.get('/database/loans/typeahead');
  },

  getLoanIdsForTypeahead: () => {
    return api.get('/database/loans/ids');
  },

  getFilteredLoans: (loanIds) => {
    return api.post('/database/loans/filtered', loanIds);
  },

  getCaseList: (loanIds) => {
    return api.post('/database/case-list', loanIds);
  },

  exportCaseList: (filters) => {
    return api.post('/database/export-case-list', filters, {
      responseType: 'blob', // Important for file downloads
    });
  },
};

// Analysis API
export const analysisAPI = {
  healthCheck: () => {
    return api.get('/analysis/health');
  },
};

// Auth API
export const login = async (username, password) => {
  const response = await api.post('/auth/login', { username, password });
  return response.data;
};

export const getCurrentUser = async () => {
  const response = await api.get('/auth/me');
  return response.data;
};

export const logout = async () => {
  const response = await api.post('/auth/logout');
  return response.data;
};

// SSO (Okta) — public helpers
export const getSsoConfig = async () => {
  const response = await api.get('/auth/sso/config');
  return response.data;
};

export const getSsoStartUrl = (returnTo) => {
  const apiUrl = import.meta.env.VITE_API_URL || 'http://127.0.0.1:8000/api';
  const base = `${apiUrl}/auth/sso/start`;
  const q = returnTo ? `?return_to=${encodeURIComponent(returnTo)}` : '';
  return `${base}${q}`;
};

// User Management API
export const userManagementAPI = {
  getUsers: () => api.get('/users'),
  createUser: (userData) => api.post('/users', userData),
  updateUser: (userId, userData) => api.patch(`/users/${userId}`, userData),
  deleteUser: (userId) => api.delete(`/users/${userId}`),
};

export default api;